/**
 * web/src/components/BonusSlipPreview.tsx
 *
 * 賞与明細書プレビュー＆印刷コンポーネント
 *
 * PayslipPreview（給与明細）と同じ考え方:
 *   ・プレビューと印刷/PDF保存で同じHTMLを使用する
 *   ・通常給与の勤怠情報（対象期間・勤務日数・勤務時間・残業時間）は表示しない
 *   ・出力日/発行日は帳票に表示しない（発行ログのみ内部保存する）
 *   ・モバイルでも保存/印刷ボタンが必ず表示されるモーダル構造
 */

import { useRef } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'

// ================================================================
// 型
// ================================================================
export type BonusRecord = {
  id: string
  assistant_id: string
  assistant_name: string
  bonus_name: string
  payment_date: string          // YYYY-MM-DD
  gross_amount: number
  health_insurance: number
  pension: number
  employment_insurance: number
  other_social_insurance: number
  income_tax: number
  other_deductions: number
  deductions_total: number
  net_amount: number
  remarks: string | null
  status: 'draft' | 'confirmed' | 'paid' | 'canceled'
  issuer_name: string
}

// ================================================================
// ヘルパー（PayslipPreview.tsx と同じロジック）
// ================================================================
function fmtCurrency(n: number | null | undefined): string {
  return `¥${Number(n ?? 0).toLocaleString('ja-JP')}`
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function jstParts(d: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0)
  return { year: get('year'), month: get('month'), day: get('day') }
}

function fmtYmd(y: number, m: number, d: number): string {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d) || !y || !m || !d) return '—'
  return `${y}年${m}月${d}日`
}

function fmtSlipDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const raw = String(dateStr).trim()

  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return fmtYmd(Number(m[1]), Number(m[2]), Number(m[3]))

  m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (m) {
    const isoUtc = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}T${m[4].padStart(2, '0')}:${m[5]}:${(m[6] ?? '00').padStart(2, '0')}Z`
    const d = new Date(isoUtc)
    if (!Number.isNaN(d.getTime())) {
      const p = jstParts(d)
      return fmtYmd(p.year, p.month, p.day)
    }
  }

  const d = new Date(raw)
  if (!Number.isNaN(d.getTime())) {
    const p = jstParts(d)
    return fmtYmd(p.year, p.month, p.day)
  }

  return '—'
}

function sanitizeFilename(s: string): string {
  return s.replace(/[/\\?%*:|"<>]/g, '_').trim()
}

// ================================================================
// 帳票HTML生成
//   プレビューと印刷/PDF保存で必ず同じHTMLを使用する。
// ================================================================
function buildBonusSlipHTML(r: BonusRecord, opts?: { autoPrint?: boolean }): string {
  const title = '賞与明細書'
  const paymentDate = fmtSlipDate(r.payment_date)
  const pdfTitle = `賞与明細_${sanitizeFilename(r.bonus_name)}_${sanitizeFilename(r.assistant_name)}`

  const line = (label: string, value: string, total = false) => `
    <tr class="${total ? 'is-total' : ''}">
      <th>${escapeHtml(label)}</th>
      <td>${escapeHtml(value)}</td>
    </tr>`

  const autoPrintScript = opts?.autoPrint
    ? `<script>
    window.addEventListener('load', () => {
      setTimeout(() => { window.print() }, 300)
    })
  </script>`
    : ''

  return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(pdfTitle)}</title>
  <style>
    @page { size: A4; margin: 14mm 13mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif;
      font-size: 11px;
      line-height: 1.5;
      color: #1f2933;
      background: #f3f4f6;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .document {
      width: 210mm;
      min-height: 297mm;
      margin: 20px auto;
      padding: 14mm 13mm;
      background: #fff;
      box-shadow: 0 8px 26px rgba(15, 23, 42, 0.14);
    }

    .top-rule { height: 4px; background: #111827; margin-bottom: 16px; }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      padding-bottom: 14px;
      border-bottom: 1px solid #d8dde3;
      margin-bottom: 14px;
    }
    .title-block .eyebrow {
      font-size: 9px;
      letter-spacing: 0.16em;
      color: #6b7280;
      font-weight: 700;
      margin-bottom: 3px;
    }
    .title {
      font-size: 20px;
      letter-spacing: 0.03em;
      font-weight: 800;
      color: #111827;
    }
    .issuer-block { text-align: right; color: #374151; }
    .issuer-label {
      font-size: 9px;
      color: #6b7280;
      font-weight: 700;
      letter-spacing: 0.08em;
      margin-bottom: 2px;
    }
    .issuer-name { font-size: 12px; font-weight: 700; color: #111827; }
    .status-chip {
      display: inline-block;
      margin-top: 6px;
      padding: 2px 8px;
      border: 1px solid #d1d5db;
      border-radius: 999px;
      color: #6b7280;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
    }

    .recipient {
      display: flex;
      gap: 20px;
      align-items: flex-start;
      margin-bottom: 14px;
      padding: 12px 14px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #fbfbfa;
    }
    .recipient-name {
      font-size: 18px;
      font-weight: 800;
      color: #111827;
      min-width: 130px;
      white-space: nowrap;
      padding-top: 2px;
    }
    .info-table { flex: 1; border-collapse: collapse; }
    .info-table th, .info-table td {
      border: none;
      padding: 3px 10px 3px 0;
      text-align: left;
      font-size: 11px;
      white-space: nowrap;
    }
    .info-table th { color: #6b7280; font-weight: 600; width: 72px; }
    .info-table td { color: #111827; font-weight: 600; }

    .summary {
      display: grid;
      grid-template-columns: 1fr 1fr 1.35fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .summary-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px 12px;
      background: #fff;
    }
    .summary-card.primary { border-color: #d5e4d7; background: #f5faf6; }
    .summary-label {
      font-size: 9px;
      color: #6b7280;
      font-weight: 700;
      letter-spacing: 0.08em;
      margin-bottom: 4px;
    }
    .summary-value {
      font-size: 17px;
      text-align: right;
      font-weight: 800;
      color: #111827;
      font-variant-numeric: tabular-nums;
    }
    .summary-card.primary .summary-value { font-size: 22px; color: #1f6b3a; }

    .section {
      border: 1px solid #e1e5ea;
      border-radius: 10px;
      overflow: hidden;
      background: #fff;
      margin-bottom: 14px;
    }
    .section-title {
      padding: 7px 11px;
      font-size: 10px;
      color: #111827;
      font-weight: 800;
      letter-spacing: 0.12em;
      background: #f3f4f6;
      border-bottom: 1px solid #e1e5ea;
    }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    th, td {
      padding: 6px 10px;
      border-bottom: 1px solid #eef1f4;
      white-space: nowrap;
    }
    tr:last-child th, tr:last-child td { border-bottom: 0; }
    th { text-align: left; color: #4b5563; font-weight: 600; width: 62%; }
    td { text-align: right; color: #111827; font-weight: 600; font-variant-numeric: tabular-nums; }
    tr.is-total th, tr.is-total td {
      background: #fafafa;
      font-weight: 800;
      color: #111827;
      border-top: 1px solid #d8dde3;
    }

    .net-box {
      border: 1px solid #d5e4d7;
      border-radius: 10px;
      overflow: hidden;
      background: #f5faf6;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 12px 16px;
      text-align: right;
      margin-bottom: 14px;
    }
    .net-label { font-size: 10px; color: #4b5563; font-weight: 800; letter-spacing: 0.1em; margin-bottom: 4px; }
    .net-value { font-size: 26px; font-weight: 900; color: #1f6b3a; font-variant-numeric: tabular-nums; }

    .remarks {
      border: 1px solid #e1e5ea;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 14px;
      background: #fff;
    }
    .remarks-body {
      padding: 10px 12px;
      white-space: pre-wrap;
      color: #374151;
      line-height: 1.65;
      font-size: 10px;
      min-height: 1.65em;
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 28px;
      padding-top: 10px;
      border-top: 1px solid #d8dde3;
      color: #4b5563;
      font-size: 10.5px;
    }
    .footer strong { color: #111827; font-weight: 800; }

    .draft-mark {
      position: fixed;
      top: 42%;
      left: 29%;
      font-size: 68px;
      font-weight: 900;
      color: rgba(125, 32, 32, 0.09);
      transform: rotate(-28deg);
      pointer-events: none;
      letter-spacing: 0.14em;
    }

    @media print {
      body { background: #fff; }
      .document { width: auto; min-height: auto; margin: 0; padding: 0; box-shadow: none; }
    }
  </style>
</head>
<body>
  ${r.status === 'draft' ? '<div class="draft-mark">下書き</div>' : ''}
  <div class="document">
    <div class="top-rule"></div>

    <header class="header">
      <div class="title-block">
        <div class="eyebrow">BONUS STATEMENT</div>
        <div class="title">${escapeHtml(title)}</div>
      </div>
      <div class="issuer-block">
        <div class="issuer-label">発行者</div>
        <div class="issuer-name">${escapeHtml(r.issuer_name)}</div>
        ${r.status === 'draft' ? '<div class="status-chip">DRAFT</div>' : ''}
      </div>
    </header>

    <section class="recipient">
      <div class="recipient-name">${escapeHtml(r.assistant_name)} 様</div>
      <table class="info-table">
        <tr>
          <th>賞与名</th>
          <td>${escapeHtml(r.bonus_name)}</td>
        </tr>
        <tr>
          <th>支給日</th>
          <td>${escapeHtml(paymentDate)}</td>
        </tr>
      </table>
    </section>

    <section class="summary">
      <div class="summary-card">
        <div class="summary-label">賞与総額</div>
        <div class="summary-value">${fmtCurrency(r.gross_amount)}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">控除額合計</div>
        <div class="summary-value">${fmtCurrency(r.deductions_total)}</div>
      </div>
      <div class="summary-card primary">
        <div class="summary-label">差引支給額</div>
        <div class="summary-value">${fmtCurrency(r.net_amount)}</div>
      </div>
    </section>

    <section class="section">
      <div class="section-title">控除</div>
      <table>
        ${line('健康保険', fmtCurrency(r.health_insurance))}
        ${line('厚生年金', fmtCurrency(r.pension))}
        ${line('雇用保険', fmtCurrency(r.employment_insurance))}
        ${line('その他社会保険', fmtCurrency(r.other_social_insurance))}
        ${line('源泉所得税', fmtCurrency(r.income_tax))}
        ${line('その他控除', fmtCurrency(r.other_deductions))}
        ${line('控除額合計', fmtCurrency(r.deductions_total), true)}
      </table>
    </section>

    <div class="net-box">
      <div class="net-label">差引支給額</div>
      <div class="net-value">${fmtCurrency(r.net_amount)}</div>
    </div>

    <section class="remarks">
      <div class="section-title">備考</div>
      <div class="remarks-body">${escapeHtml(r.remarks ?? '')}</div>
    </section>

    <footer class="footer">
      <div>発行者　<strong>${escapeHtml(r.issuer_name)}</strong></div>
    </footer>
  </div>
  ${autoPrintScript}
</body>
</html>`
}

// ================================================================
// 印刷ウィンドウを開く（フォールバック用。通常は iframe.contentWindow.print() を使う）
// ================================================================
export function openBonusSlipPrint(record: BonusRecord) {
  const html = buildBonusSlipHTML(record, { autoPrint: true })
  const win  = window.open('', '_blank', 'width=900,height=1200')
  if (!win) {
    alert('ポップアップがブロックされています。ブラウザの設定でポップアップを許可してください。')
    return
  }
  win.document.write(html)
  win.document.close()
}

// ================================================================
// プレビューモーダル（印刷と同じHTMLを iframe で表示）
// ================================================================
type Props = {
  record: BonusRecord
  onClose: () => void
  onLogIssue?: () => void
}

export function BonusSlipPreview({ record, onClose, onLogIssue }: Props) {
  const previewHtml = buildBonusSlipHTML(record, { autoPrint: false })
  const isMobile = useIsMobile()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow
    if (win) {
      win.focus()
      win.print()
    } else {
      openBonusSlipPrint(record)
    }
    onLogIssue?.()
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ alignItems: isMobile ? 'flex-end' : 'center' }}>
      <div className="modal" style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: isMobile ? '100%' : 980,
        width: isMobile ? '100%' : '94vw',
        margin: isMobile ? 0 : undefined,
        borderRadius: isMobile ? '14px 14px 0 0' : undefined,
        maxHeight: isMobile ? '90dvh' : '90vh',
        height: isMobile ? '90dvh' : '90vh',
        overflow: 'hidden',
        ...(isMobile ? { position: 'fixed' as const, bottom: 0, left: 0, right: 0 } : {}),
      }}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="modal-title">
            賞与明細プレビュー — {record.bonus_name} / {record.assistant_name}
            {record.status === 'draft' && (
              <span className="badge badge-gray" style={{ marginLeft: 8, fontSize: 10 }}>下書き</span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ flex: 1, minHeight: 0, padding: 0, background: '#f3f4f6' }}>
          <iframe
            ref={iframeRef}
            title="賞与明細プレビュー"
            srcDoc={previewHtml}
            style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#f3f4f6' }}
          />
        </div>

        <div className="modal-footer" style={{
          flexShrink: 0,
          paddingBottom: isMobile ? 'calc(14px + env(safe-area-inset-bottom, 0px))' : undefined,
        }}>
          <button className="btn btn-outline" onClick={onClose}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
            閉じる
          </button>
          <button className="btn btn-primary" onClick={handlePrint}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
            🖨 印刷 / PDFとして保存
          </button>
        </div>
      </div>
    </div>
  )
}
